// Shared look of the choice controls: Check, Option, RadioButton, RadioGroup and Toggle.
// These components take their colours, hover/focus overlay, label look and outer margins from here,
// so change them in this file instead of in the components. controlLabel is the label of every non-text control.
import {CSSProperties} from 'react'

import Colors from '@utils/Colors.js'

import {hoverOverlay} from '../styles.js'
import {field, formColors} from './styles.js'

type ChoiceMargins = Pick<CSSProperties, 'marginLeft' | 'marginRight' | 'marginBottom'>
type ChoiceComponent = 'check' | 'option' | 'radioButton' | 'radioGroup' | 'toggle'

// Outer margins of each component: none, spacing comes from FormStack/FormRow. This is the one place to change them
const margins: Record<ChoiceComponent, ChoiceMargins> = {
    check: {marginLeft: 0, marginRight: 0, marginBottom: 0},
    option: {marginLeft: 0, marginRight: 0, marginBottom: 0},
    radioButton: {marginLeft: 0, marginRight: 0, marginBottom: 0},
    radioGroup: {marginLeft: 0, marginRight: 0, marginBottom: 0},
    toggle: {marginLeft: 0, marginRight: 0, marginBottom: 0}
}

export const choiceTokens = {
    // Checked box, dot, toggle track and selected option
    checkedColor: Colors.buttonSolid,
    // Checked but disabled: the same shape in a neutral grey, so the checked state stays visible
    checkedDisabledColor: Colors.grey50,
    // Check mark, toggle knob and selected option text drawn on top of the checked colour
    checkedForeground: Colors.white,
    checkedDisabledForeground: Colors.white,

    // Same as the text fields
    uncheckedBackground: formColors.background,
    uncheckedDisabledBackground: formColors.backgroundDisabled,
    uncheckedBorder: formColors.border,

    // Option is a filled circle with its label inside, so unchecked it has a fill instead of a border
    optionUncheckedBackground: Colors.grey40,
    optionUncheckedForeground: Colors.textDark,
    optionDisabledForeground: Colors.grey60,

    // Toggle track is grey instead of white when off. Off and disabled the knob is a light grey instead of white, so it
    // reads as inactive without looking switched on
    toggleUncheckedTrack: Colors.grey20,
    toggleKnob: Colors.white,
    toggleUncheckedDisabledKnob: Colors.grey40,

    // RadioGroup is a segmented control: the selected segment is a white pill on a grey bar, not a checked colour
    groupBackground: Colors.grey30,
    groupDisabledBackground: Colors.grey20,
    groupSelectedBackground: Colors.backgroundWhite,
    groupSelectedDisabledBackground: Colors.grey40,
    groupForeground: Colors.textMedium,
    groupSelectedForeground: Colors.textDark,

    // Padding of the grey bar around the segments, the segments fill the rest of the height
    groupPadding: 3,

    // Round highlight behind the box while hovered or focused, centred in the row
    overlayColor: hoverOverlay.default,
    overlaySize: 36,

    // Row height of a single control: the same as a field, so controls line up with fields in a FormRow
    height: field.height,

    // Space between the box and its label
    labelGap: {
        check: 12, // right of the label
        radioButton: {left: 0, right: 20},
        toggle: 12 // between the label and the toggle
    },

    // Outer margins per component (see margins above); the style prop still overrides them per use
    margins
}

interface ChoiceState {
    checked?: boolean
    disabled?: boolean
}

export interface ChoiceBoxColors {
    background: string
    border: string
    foreground: string
}

// Colours of a box/track for the given state: fill, border and whatever is drawn on top (check mark, knob)
export const getChoiceBoxColors = ({checked, disabled}: ChoiceState): ChoiceBoxColors => {
    if (checked) {
        const color = disabled ? choiceTokens.checkedDisabledColor : choiceTokens.checkedColor

        return {
            background: color,
            border: color,
            foreground: disabled ? choiceTokens.checkedDisabledForeground : choiceTokens.checkedForeground
        }
    }

    return {
        background: disabled ? choiceTokens.uncheckedDisabledBackground : choiceTokens.uncheckedBackground,
        border: choiceTokens.uncheckedBorder,
        foreground: 'transparent'
    }
}

// Round hover/focus highlight around the box; it also is the click target
export const getChoiceOverlayStyle = ({highlighted, interactive}: {highlighted?: boolean, interactive: boolean}): CSSProperties => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    background: interactive && highlighted ? choiceTokens.overlayColor : 'transparent',
    width: choiceTokens.overlaySize,
    height: choiceTokens.overlaySize,
    borderRadius: '50%',
    cursor: interactive ? 'pointer' : 'default'
})

// Label of every non-text control (Check, RadioButton, Toggle, RadioGroup, Slider, ColorPicker, FileUpload,
// UploadImage), see ControlLabel. Next to the control or above it (with gapAbove below it)
export const controlLabel = {
    fontSize: 14,
    fontWeight: 400,
    letterSpacing: 0.4,
    lineHeight: 1.5,
    color: Colors.buttonSolid,
    colorDisabled: formColors.textDisabled,
    // Space between a label above its control (RadioGroup, Slider) and the control
    gapAbove: 4
}
